import { CarsFill, FlightsFill, HotelsFill } from '@pcln/brand'
import { isAfter, isToday } from 'date-fns'
import { format } from 'date-fns/format'
import { parse } from 'date-fns/parse'
import { parseISO } from 'date-fns/parseISO'

import analytics from '@/shared-utils/analytics'
import { resizeAndOptimizeImage } from '@/shared-utils/resizeAndOptimizeImage'
import toggleFavoriteHotel from '@/shared-utils/toggleFavoriteHotel'
import type { FavoriteHotel, LOCATION_SEARCH_TYPE } from '@/types'

import type { TripGroupingApiResponse } from '../TripGrouping/types'
import { fireRecentSearchCardClickEvent, fireViewAllButtonClickEvent } from './ga4'
import type {
  AbandonedCarSelectionApiResponse,
  AbandonedFlightPath,
  AbandonedFlightSelectionApiResponse,
  AbandonedHotelSelection,
  AbandonedHotelSelectionApiResponse,
  AbandonedPackageProductType,
  AbandonedPackageSelectionApiResponse,
  AbandonedPriceBreakersHotel,
  AbandonedSelection,
  AbandonedSelectionApiResponse,
  RecentTripSearches,
  ViewedHotelSelectionApiResponse,
  RTLHotel,
  SopqHotel,
} from './types'

const DAY_DATE_FORMAT = 'E, MMM dd'
export const DAY_DATE_YEAR_FORMAT = 'EEE, MMM dd, yyyy'

function getDates({ startDate, endDate = '' }: { startDate: string; endDate?: string | null }) {
  const formatStartDate = startDate.split('T')[0]
  const formatEndDate = endDate?.split('T')[0]
  // ONE-WAY
  if (!formatEndDate) {
    return [
      format(parse(formatStartDate, 'yyyy-MM-dd', new Date()), 'yyyyMMdd'),
      format(parse(formatStartDate, 'yyyy-MM-dd', new Date()), 'yyyyMMdd'),
    ]
  }
  return [
    format(parse(formatStartDate, 'yyyy-MM-dd', new Date()), 'yyyyMMdd'),
    format(parse(formatEndDate, 'yyyy-MM-dd', new Date()), 'yyyyMMdd'),
  ]
}

function formatDates({ startDate = '', endDate = '', dateFormat = DAY_DATE_FORMAT }) {
  return [
    format(parse(startDate, 'yyyyMMdd', new Date()), dateFormat),
    format(parse(endDate, 'yyyyMMdd', new Date()), dateFormat),
  ]
}

export function getRecentSearchDisplayProps(item: RecentTripSearches) {
  switch (item.productType) {
    case 'FLY': {
      const {
        travelStartDate,
        travelEndDate,
        listingsUrl,
        tripType,
        originAirportInfo,
        destinationAirportInfo,
      } = item

      const [startDate, endDate] = getDates({
        startDate: travelStartDate,
        endDate: travelEndDate,
      })
      const [formattedStartDate, formattedEndDate] = formatDates({
        startDate,
        endDate,
      })
      const {
        city: originCity,
        name: originName,
        state: originState,
        country: originCountry,
      } = originAirportInfo
      const {
        city: destinationCity,
        name: destinationName,
        state: destinationState,
        country: destinationCountry,
      } = destinationAirportInfo
      return {
        icon: (
          <div style={{ lineHeight: '0px' }}>
            <FlightsFill size='24px' />
          </div>
        ),
        overline: 'Flight',
        heading: `${
          tripType === 'ROUND_TRIP' ? `${originCity || originName}, ${originState || originCountry} - ` : ``
        }${destinationCity || destinationName}, ${destinationState || destinationCountry}`,
        actionText: `${formattedStartDate}${item.tripType === 'ROUND_TRIP' ? ` - ${formattedEndDate}` : ``}`,
        relativeUrl: listingsUrl,
      }
    }
    case 'DRIVE': {
      const { pickupDateTime, returnDateTime, pickupLocationName, returnLocationName, listingsUrl } = item
      const [startDate, endDate] = getDates({
        startDate: pickupDateTime,
        endDate: returnDateTime,
      })
      const [formattedStartDate, formattedEndDate] = formatDates({
        startDate,
        endDate,
      })
      return {
        icon: (
          <div style={{ lineHeight: '0px' }}>
            <CarsFill size='24px' />
          </div>
        ),
        overline: 'Cars',
        heading: `${pickupLocationName}${
          pickupLocationName !== returnLocationName ? ` - ${returnLocationName}` : ''
        }`,
        actionText: `${formattedStartDate} - ${formattedEndDate}`,
        relativeUrl: listingsUrl,
      }
    }
    default: {
      const { cityName, checkInDate, checkOutDate, listingsUrl } = item
      const [startDate, endDate] = getDates({
        startDate: checkInDate,
        endDate: checkOutDate,
      })
      const [formattedStartDate, formattedEndDate] = formatDates({
        startDate,
        endDate,
      })
      return {
        icon: (
          <div style={{ lineHeight: '0px' }}>
            <HotelsFill size='24px' />
          </div>
        ),
        overline: 'Hotels',
        heading: cityName,
        actionText: `${formattedStartDate} - ${formattedEndDate}`,
        relativeUrl: listingsUrl,
      }
    }
  }
}

function transformAbandonedCarApiResponseToProps(item: AbandonedCarSelectionApiResponse) {
  const {
    averageRating,
    carURL,
    carType,
    carExample,
    numberOfReviews,
    pickupLocation,
    pickupDateTime,
    returnDateTime,
    isOffAirportPickup,
    isDropOffAtDifferentLocation,
    retailTypeCarCompanyDetails,
    passengerCapacity,
    returnLocation,
    imageURL,
    genericListingsUrl,
  } = item
  const parsedCheckInDate = parse(pickupDateTime, "yyyy-MM-dd'T'HH:mm:ss", new Date())
  const formattedCheckInDate = format(parsedCheckInDate, "EEE, MMM dd, yyyy 'at' h:mmaaa")
  const parsedCheckOutDate = parse(returnDateTime, "yyyy-MM-dd'T'HH:mm:ss", new Date())
  const formattedCheckOutDate = format(parsedCheckOutDate, "EEE, MMM dd, yyyy 'at' h:mmaaa")
  const pickUpDateInfo = {
    date: format(parsedCheckInDate, 'EEE, MMM dd, yyyy'),
    time: format(parsedCheckInDate, 'h:mmaaa'),
  }
  const dropOffDateInfo = {
    date: format(parsedCheckOutDate, 'EEE, MMM dd, yyyy'),
    time: format(parsedCheckOutDate, 'h:mmaaa'),
  }

  return {
    headline: carType,
    expressDealText: carExample,
    pickupLocation,
    isDropOffAtDifferentLocation,
    returnLocation,
    specifyOffAirport: isOffAirportPickup,
    pickUpDate: formattedCheckInDate,
    dropOffDate: formattedCheckOutDate,
    numAdults: passengerCapacity.toString(),
    numAdultsCount: passengerCapacity,
    linkUrl: carURL,
    genericListingsUrl,
    numReviews: numberOfReviews.toLocaleString(),
    numReviewsCount: numberOfReviews,
    overallRating: averageRating !== undefined ? String(averageRating) : undefined,
    overallRatingValue: averageRating,
    imageSrc: imageURL,
    rcDealType: 'RETAIL',
    productType: 'cars' as const,
    pickUpDateInfo,
    dropOffDateInfo,
    brandImg: retailTypeCarCompanyDetails?.brandImageURL
      ? `${retailTypeCarCompanyDetails.brandImageURL}&opto&auto=webp&width=176`
      : undefined,
  }
}

function isSopqHotel(hotelData: RTLHotel | SopqHotel | AbandonedPriceBreakersHotel): hotelData is SopqHotel {
  return hotelData.__typename === 'SopqHotel'
}

function isPriceBreaker(
  hotelData: RTLHotel | SopqHotel | AbandonedPriceBreakersHotel,
): hotelData is AbandonedPriceBreakersHotel {
  return hotelData.__typename === 'AbandonedPriceBreakersHotel'
}
function transformAbandonedHotelApiResponseToProps(item: AbandonedHotelSelectionApiResponse) {
  const {
    hotelURL,
    genericListingsUrl,
    checkInDate,
    checkOutDate,
    numberOfAdults,
    numberOfRooms,
    hotelPriceAtAbandonment,
    hotelTotalPriceWithTaxesAndFeesAtAbandonment,
    hotelTotalPriceExcludingTaxes,
    priceDisplayRegulation,
  } = item

  const parsedCheckInDate = parse(checkInDate, 'yyyy-MM-dd', new Date())
  const formattedCheckInDate = format(parsedCheckInDate, 'EEE, MMM dd, yyyy')
  const parsedCheckOutDate = parse(checkOutDate, 'yyyy-MM-dd', new Date())
  const formattedCheckOutDate = format(parsedCheckOutDate, 'EEE, MMM dd, yyyy')
  const commonDetails = {
    checkInDate: formattedCheckInDate,
    checkOutDate: formattedCheckOutDate,
    numAdults: numberOfAdults,
    numRooms: numberOfRooms,
    linkUrl: hotelURL,
    genericListingsUrl,
    productType: 'hotels' as const,
    hotelPriceAtAbandonment,
    hotelTotalPriceWithTaxesAndFeesAtAbandonment,
    hotelTotalPriceExcludingTaxes,
    priceDisplayRegulation,
  }

  const hotelData = item.hotelInfo

  if (isSopqHotel(hotelData)) {
    const { __typename, images, name, starRating, neighborhood, location } = hotelData

    const selectedImage = images.find((img) => img.type === 'GENERIC') ?? images[0]
    const imageSrc = selectedImage?.fastlyUrl ? resizeAndOptimizeImage(selectedImage.fastlyUrl) : undefined
    const neighborhoodName = neighborhood?.name || ''
    const locationName = location?.name || ''
    const hotelDescription = name || (starRating ? `A ${starRating} Star Hotel` : 'A Hotel')
    return {
      ...commonDetails,
      imageSrc,
      hotelDealType: __typename,
      headline: `${hotelDescription} in ${neighborhoodName}`,
      subheadline: locationName,
    }
  }

  if (isPriceBreaker(hotelData)) {
    const { __typename, pricebreakerDescription, pricebreakerLocation } = hotelData
    return {
      ...commonDetails,
      hotelDealType: __typename,
      headline: pricebreakerDescription,
      subheadline: pricebreakerLocation,
    }
  }

  const { __typename, id, images, isFavorite, location, name, neighborhood, reviewInfo, starRating } =
    hotelData

  const imageSrc = resizeAndOptimizeImage(images[0].fastlyUrl)
  const rating = starRating || ''
  const totalReviews = reviewInfo?.reviewSummary?.total?.count || 0
  const overall = reviewInfo?.reviewSummary?.scores?.[0]?.score || ''
  const neighborhoodName = neighborhood?.name || ''

  return {
    ...commonDetails,
    hotelDealType: __typename,
    headline: name || '',
    subheadline: neighborhoodName,
    numReviews: totalReviews,
    numStars: parseInt(rating, 10),
    overallRating: parseFloat(overall),
    imageSrc,
    id,
    isFavorite,
    cityId: location?.id,
  }
}

export function getAbandonedHotelQdpPrice({
  hotelPriceAtAbandonment,
  hotelTotalPriceWithTaxesAndFeesAtAbandonment,
  hotelTotalPriceExcludingTaxes,
  priceDisplayRegulation,
}: Pick<
  AbandonedHotelSelection,
  | 'hotelPriceAtAbandonment'
  | 'hotelTotalPriceWithTaxesAndFeesAtAbandonment'
  | 'hotelTotalPriceExcludingTaxes'
  | 'priceDisplayRegulation'
>): number | undefined {
  let amount = hotelPriceAtAbandonment?.amount
  if (priceDisplayRegulation === 'TAX_EXCL_TOTAL_PRICE_PROMINENT') {
    amount = hotelTotalPriceExcludingTaxes?.amount ?? amount
  } else if (priceDisplayRegulation === 'TOTAL_PRICE_PROMINENT') {
    amount = hotelTotalPriceWithTaxesAndFeesAtAbandonment?.amount ?? amount
  }
  if (!amount) return undefined

  const price = Math.floor(Number(amount))
  return Number.isNaN(price) ? undefined : price
}

export function appendQdpParam({
  linkUrl,
  selection,
}: {
  linkUrl: string
  selection: AbandonedHotelSelection
}): string {
  const qdp = getAbandonedHotelQdpPrice(selection)
  if (qdp === undefined) return linkUrl

  try {
    const url = new URL(linkUrl)
    url.searchParams.set('qdp', String(qdp))
    return url.toString()
  } catch {
    return linkUrl
  }
}

function isTripDateValid(tripDate: string, formatString: string) {
  try {
    const parseTripDate = parse(tripDate, formatString, new Date())

    return isToday(parseTripDate) || isAfter(parseTripDate, new Date())
  } catch (err) {
    analytics.logError({
      message: 'Error parsing abandoned selection date',
      errorMessage: err instanceof Error ? err.message : String(err),
      logObject: {
        error: JSON.stringify(err),
      },
    })

    return false
  }
}

function isValidAbandonedHotelSelection(
  selection: AbandonedSelectionApiResponse,
): selection is AbandonedHotelSelectionApiResponse {
  return (
    'hotelURL' in selection && isTripDateValid(selection.checkInDate, 'yyyy-MM-dd') && !selection.isBooked
  )
}

function getCityIdFromAbandonedHotelSelection(selection: AbandonedHotelSelectionApiResponse): string | null {
  const { hotelInfo } = selection
  if (hotelInfo.__typename === 'RtlHotel' && hotelInfo.location?.id) {
    return String(hotelInfo.location.id)
  }
  return null
}

export function getHotelIdFromRelaxAtUrl(hotelURL: string): string | null {
  const match = /\/relax\/at\/(\d+)/.exec(hotelURL)
  return match?.[1] ?? null
}

function getAbandonedHotelId(selection: AbandonedHotelSelectionApiResponse): string | null {
  const { hotelInfo, hotelURL } = selection
  if (hotelInfo.__typename === 'RtlHotel' && hotelInfo.id) {
    return String(hotelInfo.id)
  }
  return getHotelIdFromRelaxAtUrl(hotelURL)
}

function getAbandonedHotelDisplayName(selection: AbandonedHotelSelectionApiResponse): string | null {
  const { hotelInfo } = selection
  if (hotelInfo.__typename === 'RtlHotel') {
    const name = hotelInfo.name?.trim()
    return name || null
  }
  if (hotelInfo.__typename === 'SopqHotel') {
    const name = hotelInfo.name?.trim()
    if (name) return name
    return hotelInfo.starRating ? `A ${hotelInfo.starRating} Star Hotel` : null
  }
  if (hotelInfo.__typename === 'AbandonedPriceBreakersHotel') {
    return hotelInfo.pricebreakerDescription?.trim() || null
  }
  return null
}

export type HotelTypeaheadEntry = {
  cityId: string
  displayName: string | null
  hotelId: string | null
}

/** Newest activity first. Missing/invalid timestamps sort last. */
export function sortByCreationDateTimeDesc<T extends { creationDateTime?: string }>(
  items: ReadonlyArray<T>,
): T[] {
  return [...items].sort((a, b) => {
    const aTime = a.creationDateTime ? Date.parse(a.creationDateTime) : Number.NaN
    const bTime = b.creationDateTime ? Date.parse(b.creationDateTime) : Number.NaN
    const aValid = !Number.isNaN(aTime)
    const bValid = !Number.isNaN(bTime)
    if (aValid && bValid) return bTime - aTime
    if (aValid) return -1
    if (bValid) return 1
    return 0
  })
}

/** Collapses repeated abandoned rows for the same hotel (newest first, then first occurrence wins). */
export function dedupeAbandonedHotelSelectionsByHotelId(
  selections: ReadonlyArray<AbandonedSelectionApiResponse>,
): AbandonedHotelSelectionApiResponse[] {
  const seenHotelIds = new Set<string>()
  const deduped: AbandonedHotelSelectionApiResponse[] = []

  for (const selection of sortByCreationDateTimeDesc(selections.filter(isValidAbandonedHotelSelection))) {
    const hotelId = getAbandonedHotelId(selection)
    if (!hotelId || seenHotelIds.has(hotelId)) continue
    seenHotelIds.add(hotelId)
    deduped.push(selection)
  }

  return deduped
}

/** Returns unique abandoned hotels for typeahead (hotel id + label), preserving API order. */
export function getAbandonedHotelTypeaheadEntries(
  selections: ReadonlyArray<AbandonedSelectionApiResponse>,
  maxCount: number,
): HotelTypeaheadEntry[] {
  const entries: HotelTypeaheadEntry[] = []

  for (const selection of dedupeAbandonedHotelSelectionsByHotelId(selections)) {
    const cityId = getCityIdFromAbandonedHotelSelection(selection)
    if (!cityId) continue
    entries.push({
      cityId,
      displayName: getAbandonedHotelDisplayName(selection),
      hotelId: getAbandonedHotelId(selection),
    })
    if (entries.length >= maxCount) {
      break
    }
  }

  return entries
}

/** Returns unique city IDs from valid abandoned hotel selections, preserving API order. */
export function getAbandonedHotelCityIds(
  selections: ReadonlyArray<AbandonedSelectionApiResponse>,
  maxCount: number,
): string[] {
  const cityIds: string[] = []
  const seen = new Set<string>()
  for (const entry of getAbandonedHotelTypeaheadEntries(selections, maxCount)) {
    if (seen.has(entry.cityId)) continue
    seen.add(entry.cityId)
    cityIds.push(entry.cityId)
  }
  return cityIds
}

function isValidViewedHotelSelection(
  selection: ViewedHotelSelectionApiResponse,
): selection is ViewedHotelSelectionApiResponse {
  return selection.hotelInfo?.__typename === 'RtlHotel' && Boolean(selection.hotelInfo.location?.id)
}

function getViewedHotelId(selection: ViewedHotelSelectionApiResponse): string | null {
  const { hotelInfo, url } = selection
  if (hotelInfo.__typename === 'RtlHotel' && hotelInfo.id) {
    return String(hotelInfo.id)
  }
  return getHotelIdFromRelaxAtUrl(url)
}

/** Collapses repeated viewed rows for the same hotel (newest first, then first occurrence wins). */
export function dedupeViewedHotelSelectionsByHotelId(
  selections: ReadonlyArray<ViewedHotelSelectionApiResponse>,
): ViewedHotelSelectionApiResponse[] {
  const seenHotelIds = new Set<string>()
  const deduped: ViewedHotelSelectionApiResponse[] = []

  for (const selection of sortByCreationDateTimeDesc(selections.filter(isValidViewedHotelSelection))) {
    const hotelId = getViewedHotelId(selection)
    if (!hotelId || seenHotelIds.has(hotelId)) continue
    seenHotelIds.add(hotelId)
    deduped.push(selection)
  }

  return deduped
}

/** Returns unique viewed hotels for typeahead (city id + hotel label), preserving API order. */
export function getViewedHotelTypeaheadEntries(
  selections: ReadonlyArray<ViewedHotelSelectionApiResponse>,
  maxCount: number,
): HotelTypeaheadEntry[] {
  const entries: HotelTypeaheadEntry[] = []

  for (const selection of dedupeViewedHotelSelectionsByHotelId(selections)) {
    const cityId = String(selection.hotelInfo.location!.id!)
    const hotelId = getViewedHotelId(selection)
    if (!hotelId) continue
    const displayName =
      selection.hotelInfo.__typename === 'RtlHotel' ? selection.hotelInfo.name?.trim() || null : null
    entries.push({
      cityId,
      displayName,
      hotelId,
    })
    if (entries.length >= maxCount) {
      break
    }
  }

  return entries
}

export function mapHotelTypeaheadEntriesToLocations(
  entries: ReadonlyArray<HotelTypeaheadEntry>,
  cityInfoList: LOCATION_SEARCH_TYPE[],
): LOCATION_SEARCH_TYPE[] {
  const cityInfoById = new Map<string, LOCATION_SEARCH_TYPE>()
  for (const cityInfo of cityInfoList) {
    const cityId = String(cityInfo.cityID ?? cityInfo.id ?? '')
    if (cityId && !cityInfoById.has(cityId)) {
      cityInfoById.set(cityId, cityInfo)
    }
  }

  return entries.flatMap((entry) => {
    const item = cityInfoById.get(entry.cityId)
    if (!item) return []
    const label = entry.displayName?.trim() || item.itemName
    const hotelId = entry.hotelId?.trim()
    const isHotelSelection = Boolean(hotelId)
    const locationId = hotelId ?? item.id
    return [
      {
        ...item,
        type: isHotelSelection ? 'HOTEL' : item.type,
        id: locationId,
        cityID: entry.cityId,
        itemName: label,
        displayLine1: label,
        country: item.countryName,
        entered: '',
        highlightedName: null,
      },
    ]
  })
}

function isValidAbandonedCarSelection(
  selection: AbandonedSelectionApiResponse,
): selection is AbandonedCarSelectionApiResponse {
  return (
    'carURL' in selection &&
    isTripDateValid(selection.pickupDateTime, "yyyy-MM-dd'T'HH:mm:ss") &&
    !selection.isBooked
  )
}

function isValidAbandonedFlightSelection(
  selection: AbandonedSelectionApiResponse,
): selection is AbandonedFlightSelectionApiResponse {
  return (
    'flightURL' in selection &&
    isTripDateValid(selection.originDestinationInfo.travelStartDateTime, "yyyy-MM-dd'T'HH:mm:ss") &&
    !selection.isBooked
  )
}

function getOriginDestAirportCodes(flightPath: AbandonedFlightPath[]) {
  return {
    departOriginAirportCode: flightPath[0].originAirportCode,
    departDestAirportCode: flightPath[0].destAirportCode,
    returnOriginAirportCode: flightPath[flightPath.length - 1].originAirportCode,
    returnDestAirportCode: flightPath[flightPath.length - 1].destAirportCode,
  }
}

export function transformAbandonedFlightApiResponseToProps(item: AbandonedFlightSelectionApiResponse) {
  const {
    travelStartDateTime,
    travelEndDateTime,
    originAirportInfo: {
      city: originCity = '',
      state: originState = '',
      country: originCountry,
      code: originCode,
    },
    destinationAirportInfo: {
      city: destCity = '',
      state: destState = '',
      country: destCountry,
      code: destCode,
    },
  } = item.originDestinationInfo
  const [startDate, endDate] = getDates({
    startDate: travelStartDateTime,
    endDate: travelEndDateTime,
  })
  const [formattedStartDate, formattedEndDate] = formatDates({
    startDate,
    endDate,
    dateFormat: 'E, MMM dd, yyyy',
  })
  const { departOriginAirportCode, departDestAirportCode, returnOriginAirportCode, returnDestAirportCode } =
    getOriginDestAirportCodes(item.originDestinationInfo.flightPath)
  return {
    headline: `${originCity}, ${
      originState || originCountry
    } (${originCode}) - ${destCity}, ${destState || destCountry} (${destCode})`,
    linkUrl: item.flightURL,
    genericListingsUrl: item.genericListingsUrl,
    productType: 'flights' as const,
    tripType: item.tripType,
    dealType: item.dealType,
    departDate: formattedStartDate,
    returnDate: formattedEndDate,
    departOriginAirportCode,
    departDestAirportCode,
    returnOriginAirportCode,
    returnDestAirportCode,
    numAdults: item.numberOfAdults,
    cabinClass: item.cabinClass,
  }
}

function isValidAbandonedPackageSelection(
  selection: AbandonedSelectionApiResponse,
): selection is AbandonedPackageSelectionApiResponse {
  if (!('__typename' in selection) || selection.__typename !== 'AbandonedPackage' || selection.isBooked) {
    return false
  }
  const hotel = selection.hotelSelections[0]
  if (!hotel) return false
  return isTripDateValid(hotel.checkInDate, 'yyyy-MM-dd') && !hotel.isBooked
}

const PACKAGE_TYPE_MAP: Record<string, AbandonedPackageProductType[]> = {
  HOTEL_AIR: ['Hotel', 'Flight'],
  HOTEL_RC: ['Hotel', 'Car'],
  HOTEL_AIR_RC: ['Hotel', 'Flight', 'Car'],
  AIR_RC: ['Flight', 'Car'],
}

const PACKAGE_SUBTITLE_MAP: Record<string, string> = {
  HOTEL_AIR: 'Hotel + Flight Package',
  HOTEL_RC: 'Hotel + Rental Car Package',
  HOTEL_AIR_RC: 'Flight + Hotel + Rental Car Package',
  AIR_RC: 'Flight + Rental Car Package',
}

export function normalizePackageType(raw: string): string {
  return raw.toUpperCase().replaceAll('_CAR', '_RC')
}

function buildLocationLabel(city?: string, state?: string, country?: string): string {
  if (!city) return ''
  if (state) return `${city}, ${state}`
  if (country) return `${city}, ${country}`
  return city
}

function buildPackageHeadline(item: AbandonedPackageSelectionApiResponse): string {
  const air = item.airSelections[0]
  if (air) {
    const { originAirportInfo, destinationAirportInfo } = air.originDestinationInfo
    const originLabel = buildLocationLabel(
      originAirportInfo.city,
      originAirportInfo.state,
      originAirportInfo.country,
    )
    const destLabel = buildLocationLabel(
      destinationAirportInfo.city,
      destinationAirportInfo.state,
      destinationAirportInfo.country,
    )
    if (originLabel && destLabel) return `${originLabel} to ${destLabel} Package`
  }

  const hotel = item.hotelSelections[0]
  const hotelLabel = buildLocationLabel(hotel?.cityName, hotel?.stateCode, hotel?.countryName)
  if (hotelLabel) return `${hotelLabel} Package`

  // fallback if no destination / origin
  // ie New York City, NY to Toronto, ON Package -> Vacation Package
  return 'Vacation Package'
}

function transformAbandonedPackageApiResponseToProps(item: AbandonedPackageSelectionApiResponse) {
  const hotel = item.hotelSelections[0]
  const hotelInfo = hotel.hotelInfo
  const neighborhoodName =
    'neighborhood' in hotelInfo && hotelInfo.neighborhood?.name ? hotelInfo.neighborhood.name : ''

  const parsedCheckInDate = parse(hotel.checkInDate, 'yyyy-MM-dd', new Date())
  const formattedCheckInDate = format(parsedCheckInDate, 'EEE, MMM dd, yyyy')
  const parsedCheckOutDate = parse(hotel.checkOutDate, 'yyyy-MM-dd', new Date())
  const formattedCheckOutDate = format(parsedCheckOutDate, 'EEE, MMM dd, yyyy')

  const headline = buildPackageHeadline(item)
  const normalized = normalizePackageType(item.packageType)
  const subtitle = PACKAGE_SUBTITLE_MAP[normalized] ?? 'Vacation Package'

  const adults = hotel.numberOfAdults
  const rooms = hotel.numberOfRooms
  const adultLabel = `${adults} Adult${adults === 1 ? '' : 's'}`
  const roomLabel = `${rooms} Room${rooms === 1 ? '' : 's'}`

  return {
    headline,
    subtitle,
    linkUrl: item.listingsUrl,
    genericListingsUrl: item.listingsUrl,
    productType: 'packages' as const,
    checkInDate: formattedCheckInDate,
    checkOutDate: formattedCheckOutDate,
    occupancyText: `${adultLabel}, ${roomLabel}`,
    locationText: neighborhoodName || undefined,
    packageProductTypes: PACKAGE_TYPE_MAP[normalized] ?? [],
  }
}

export function getFirstValidAbandonedSelection(
  selections: ReadonlyArray<AbandonedSelectionApiResponse>,
): AbandonedSelection | null {
  const abandonedSelection =
    selections.find((selection) => {
      if (isValidAbandonedHotelSelection(selection)) {
        return true
      }
      if (isValidAbandonedFlightSelection(selection)) {
        return true
      }
      if (isValidAbandonedCarSelection(selection)) {
        return true
      }

      return isValidAbandonedPackageSelection(selection)
    }) ?? null

  if (abandonedSelection) {
    if ('hotelURL' in abandonedSelection) {
      return transformAbandonedHotelApiResponseToProps(abandonedSelection)
    }
    if ('flightURL' in abandonedSelection) {
      return transformAbandonedFlightApiResponseToProps(abandonedSelection)
    }
    if ('carURL' in abandonedSelection) {
      return transformAbandonedCarApiResponseToProps(abandonedSelection)
    }
    if ('__typename' in abandonedSelection && abandonedSelection.__typename === 'AbandonedPackage') {
      return transformAbandonedPackageApiResponseToProps(abandonedSelection)
    }
  }

  return null
}

function isTripISODateValid(tripDate: string) {
  try {
    const parseTripDate = parseISO(tripDate)
    return isToday(parseTripDate) || isAfter(parseTripDate, new Date())
  } catch (err) {
    analytics.logError({
      message: 'Error parsing trip grouping date',
      errorMessage: err instanceof Error ? err.message : String(err),
      logObject: {
        error: JSON.stringify(err),
      },
    })

    return false
  }
}

export function getFirstValidTripGrouping(groupings: ReadonlyArray<TripGroupingApiResponse>) {
  const tripGroup = groupings.find((grouping) =>
    isTripISODateValid(grouping.bookingActivityData[0]?.travelStartDateTime),
  )

  // in case of multiple bookings, show first booking in group
  const tripGroupingBooking = tripGroup?.bookingActivityData?.[0] ?? null

  const tripGroupingAbandonedSelection = getFirstValidAbandonedSelection(
    tripGroup?.selectionActivityData ?? [],
  )

  return { tripGroupingBooking, tripGroupingAbandonedSelection }
}

export function onCardClick(
  relativeUrl: string,
  pageName: 'homepage' | 'landing',
  productType: 'FLY' | 'STAY' | 'DRIVE',
) {
  fireRecentSearchCardClickEvent({ productType: productType, pageName: pageName })
  const url = new URL(relativeUrl, window.location.origin)
  window.open(url)
}

export function onViewAllButtonClick(pageName: 'homepage' | 'landing') {
  fireViewAllButtonClickEvent(pageName)
  window.open('/next-profile/trips?tab=recent_activity')
}

export async function handleToggleFavorite(
  { hotelId, cityId, cguid, appName, appVersion }: FavoriteHotel,
  isNowFavorite: boolean,
) {
  if (!cguid) {
    return
  }

  try {
    await toggleFavoriteHotel(isNowFavorite ? 'add' : 'remove', {
      hotelId,
      cityId,
      cguid,
      appName,
      appVersion,
    })
  } catch (err) {
    analytics.logError({
      message: 'Error toggling favorite status',
      errorMessage: err instanceof Error ? err.message : String(err),
      logObject: {
        error: JSON.stringify(err),
      },
    })
  }
}
